import {
  Sparkles, Video, Megaphone, Search, Compass, ScanFace,
  Linkedin, Mail, Newspaper, Feather, Magnet, Lightbulb, RefreshCw,
  Camera, BadgeCheck, Presentation, GraduationCap, Headphones, Clapperboard,
  Target, Layers, Image, PenLine, BarChart3, FileText, Settings, MapPin, TrendingUp,
  Quote, Radio, Grid3x3, CalendarRange, Compass as CompassIcon, Globe, Palette, Bot, Database, Workflow, Zap,
} from "lucide-react";

const PROCESS = [
  { title: "Analyse", desc: "Vi dykker ned i jeres forretning, marked og mål." },
  { title: "Strategi", desc: "Vi lægger en klar plan med retning og prioriteter." },
  { title: "Produktion", desc: "Vi eksekverer og skaber det færdige indhold." },
  { title: "Optimering", desc: "Vi måler, justerer og forbedrer løbende." },
];

const WHO_FOR = [
  "Virksomheder med begrænset marketingkapacitet",
  "B2B-virksomheder",
  "Vækstvirksomheder",
  "Marketingteams, der vil skalere",
  "Direktører, der ønsker mere synlighed",
];

export const SOLUTIONS = [
  {
    slug: "aicom-ai",
    navTitle: "Aicom AI",
    navDesc: "AI-platform, der fungerer som jeres digitale kommunikationsrådgiver.",
    Icon: Sparkles,
    heroTitle: "Jeres digitale kommunikationsrådgiver",
    heroLead:
      "Aicom AI hjælper virksomheder med at planlægge, prioritere og producere professionel kommunikation - på minutter frem for dage.",
    tryFree: true,
    whatIsIt: [
      "Aicom AI er en AI-platform, der lærer jeres virksomhed at kende og løbende kommer med forslag til, hvad I bør kommunikere om.",
      "Platformen genererer blandt andet LinkedIn-opslag, nyhedsbreve, pressemeddelelser, pressevurderinger, brandfortællinger, kampagner, lead magnets og kommunikationsidéer.",
    ],
    benefits: [
      { Icon: Linkedin, title: "LinkedIn-opslag", desc: "Færdige opslag i jeres tone, klar til at poste." },
      { Icon: Mail, title: "Nyhedsbreve", desc: "Emne, overskrift og brødtekst på minutter." },
      { Icon: Newspaper, title: "Presse", desc: "Vurder historien og få en færdig pressemeddelelse." },
      { Icon: Feather, title: "Brandfortællinger", desc: "Jeres historie fortalt skarpt og konsistent." },
      { Icon: Megaphone, title: "Kampagner & lead magnets", desc: "Fra mål til færdige kampagnetekster og lead magnets." },
      { Icon: Lightbulb, title: "Kommunikationsidéer", desc: "Skræddersyede idéer til hver eneste kanal." },
      { Icon: RefreshCw, title: "Løbende forslag", desc: "Aicom foreslår, hvad I bør kommunikere om - hver uge." },
    ],
    process: PROCESS,
    whoFor: WHO_FOR,
  },
  {
    slug: "ai-avatar",
    navTitle: "AI Avatar",
    navDesc: "Professionelle AI-avatarer til video, præsentationer og undervisning.",
    Icon: ScanFace,
    heroTitle: "Få en digital version af jer selv",
    heroLead:
      "Vi udvikler professionelle AI-avatarer, der gør det muligt at producere videoindhold hurtigt, ensartet og i høj kvalitet.",
    whatIsIt: [
      "En AI-avatar er en digital version af jer, som kan bruges til præsentationer, undervisning, onboarding, kundeservice og markedsføring.",
      "Perfekt til virksomheder, der ønsker mere video uden at skulle filme hver gang - samme høje kvalitet, hver gang.",
    ],
    benefits: [
      { Icon: Camera, title: "Video uden kamera", desc: "Producér videoindhold uden at skulle filme hver gang." },
      { Icon: BadgeCheck, title: "Ensartet kvalitet", desc: "Samme høje udtryk og kvalitet i alt jeres indhold." },
      { Icon: Presentation, title: "Præsentationer", desc: "Levende præsentationer med jeres digitale vært." },
      { Icon: GraduationCap, title: "Undervisning & onboarding", desc: "Skalér intern viden og oplæring effektivt." },
      { Icon: Headphones, title: "Kundeservice", desc: "Automatisér svar med et personligt ansigt." },
      { Icon: Clapperboard, title: "Markedsføring", desc: "Skil jer ud med moderne, professionelt videoindhold." },
    ],
    process: PROCESS,
    whoFor: WHO_FOR,
  },
  {
    slug: "paid-social",
    navTitle: "Paid Social",
    navDesc: "Annoncering på LinkedIn, Facebook og Instagram med fokus på leads og salg.",
    Icon: Megaphone,
    heroTitle: "Annoncering, der skaber resultater",
    heroLead:
      "Vi hjælper virksomheder med flere leads, mere synlighed og højere salg gennem målrettede kampagner på LinkedIn, Facebook og Instagram.",
    whatIsIt: [
      "Paid Social er betalt annoncering på de sociale platforme, hvor jeres kunder allerede bruger deres tid.",
      "Fokus er altid på forretningsmæssige resultater - ikke bare likes og visninger.",
    ],
    benefits: [
      { Icon: Target, title: "Strategi", desc: "En klar plan for hvem, hvad og hvor." },
      { Icon: Layers, title: "Kampagneopsætning", desc: "Korrekt opsætning på LinkedIn, Facebook og Instagram." },
      { Icon: Image, title: "Kreativer", desc: "Scroll-stoppende visuelt indhold." },
      { Icon: PenLine, title: "Tekster", desc: "Annoncetekster, der konverterer." },
      { Icon: RefreshCw, title: "Løbende optimering", desc: "Data-drevet forbedring uge for uge." },
      { Icon: BarChart3, title: "Rapportering", desc: "Fuldt overblik over resultater og ROI." },
    ],
    process: PROCESS,
    whoFor: WHO_FOR,
  },
  {
    slug: "hjemmesider",
    navTitle: "Hjemmesider",
    navDesc: "Moderne hjemmesider, hvor strategi, design, AI og konvertering går hånd i hånd.",
    Icon: Globe,
    heroTitle: "En hjemmeside, ",
    heroTitleAccent: "der gør besøgende til kunder",
    heroLead:
      "Jeres hjemmeside er ofte det første møde med potentielle kunder. Derfor skal den ikke bare se godt ud - den skal fortælle jeres historie, skabe tillid og gøre det let at tage næste skridt. Vi udvikler moderne hjemmesider, hvor strategi, design, AI og konverteringsoptimering går hånd i hånd.",
    whatIsIt: [
      "En hjemmeside skal være virksomhedens stærkeste digitale aktiv - ikke bare se godt ud, men fortælle jeres historie, skabe tillid og gøre det let at tage næste skridt.",
      "Vi hjælper jer med at skabe en hjemmeside, der kombinerer strategi, brugeroplevelse, SEO og AI-understøttet indholdsproduktion. Målet er ikke blot flere besøgende - men flere relevante henvendelser og kunder.",
    ],
    benefits: [
      { Icon: Layers, title: "Strategisk opbygning", desc: "En klar struktur, der guider brugerne mod handling." },
      { Icon: Palette, title: "Brand og design", desc: "Et moderne design, der afspejler jeres identitet." },
      { Icon: Search, title: "SEO & AI-synlighed", desc: "Optimeret til både Google og de nye AI-drevne søgeoplevelser." },
      { Icon: TrendingUp, title: "Konverteringsoptimering", desc: "Flere besøgende bliver til kundehenvendelser." },
      { Icon: Sparkles, title: "AI-understøttet indhold", desc: "Professionelle tekster udviklet hurtigere med AI og kvalitetssikret af vores rådgivere." },
      { Icon: Settings, title: "Nem administration", desc: "Redigér nemt indhold uden tekniske kompetencer." },
    ],
    process: [
      { title: "Vi lærer virksomheden at kende", desc: "Vi sætter os ind i jeres forretning, målgrupper og ambitioner." },
      { title: "Vi udvikler løsningen", desc: "Vi designer struktur, design og indhold med udgangspunkt i jeres strategi." },
      { title: "Vi bygger og lancerer", desc: "Vi udvikler hjemmesiden og sikrer, at den fungerer optimalt på alle enheder." },
      { title: "Vi udvikler videre", desc: "Vi hjælper med SEO, AI, nyt indhold og løbende optimering." },
    ],
    whoFor: [
      "vil fremstå mere professionelle",
      "ønsker flere kundehenvendelser",
      "mangler en tydelig digital profil",
      "vil være synlige i både Google og AI-søgninger",
      "ønsker en hjemmeside, der udvikler sig sammen med virksomheden",
    ],
  },
  {
    slug: "thought-leadership",
    navTitle: "Thought Leadership",
    navDesc: "Bliv den virksomhed, kunder, medier og beslutningstagere lytter til.",
    Icon: Quote,
    heroTitle: "Bliv en stemme, markedet lytter til",
    heroLead:
      "Vi positionerer jeres virksomhed og nøglepersoner som de tydelige eksperter i jeres felt - så kunder, medier og beslutningstagere lytter, når I siger noget.",
    whatIsIt: [
      "Thought Leadership handler om at blive den virksomhed, andre henviser til, citerer og stoler på.",
      "Vi bygger jeres faglige troværdighed op gennem skarpt indhold, tydelige holdninger og vedvarende synlighed - systematisk og over tid.",
    ],
    benefits: [
      { Icon: Target, title: "Ekspertpositionering", desc: "Bliv den tydelige autoritet i jeres felt." },
      { Icon: PenLine, title: "Holdningsindlæg", desc: "Skarpe indlæg, der sætter dagsordenen." },
      { Icon: Linkedin, title: "LinkedIn-tilstedeværelse", desc: "En stærk, personlig profil, der bliver fulgt." },
      { Icon: Presentation, title: "Foredrag & webinarer", desc: "Fagligt indhold, der styrker jeres autoritet." },
      { Icon: Newspaper, title: "Presse & medieomtale", desc: "Bliv kilden, medierne ringer til." },
      { Icon: Quote, title: "Konsistent stemme", desc: "Én tydelig holdning på tværs af alle kanaler." },
    ],
    process: PROCESS,
    whoFor: WHO_FOR,
  },
  {
    slug: "seo",
    navTitle: "SEO/GEO",
    navDesc: "Bliv fundet af kunderne - både på Google og i AI-søgninger.",
    Icon: Search,
    heroTitle: "Bliv fundet - på Google og i AI-søgninger",
    heroLead:
      "SEO handler ikke kun om placeringer på Google - det handler om at blive fundet af mennesker, der allerede leder efter det, I tilbyder. Og i dag sker en stigende del af søgningerne i AI-tjenester som ChatGPT, Google AI Overviews, Perplexity og Gemini. Derfor arbejder vi med både SEO og GEO.",
    whatIsIt: [
      "Vi arbejder strategisk med jeres synlighed i søgemaskinerne, så I tiltrækker flere relevante besøgende. Målet er flere relevante besøgende og flere kundehenvendelser - ikke bare trafik for trafikkens skyld.",
      "GEO (Generative Engine Optimization) er den nye disciplin ved siden af SEO. Hvor SEO handler om at rangere højt i Googles resultater, handler GEO om at blive nævnt, citeret og anbefalet, når kunderne spørger en AI-tjeneste som ChatGPT, Perplexity, Gemini eller Googles AI-svar. Vi sørger for, at jeres virksomhed er synlig begge steder - så I ikke forsvinder, når søgningen flytter fra Google til AI.",
    ],
    benefits: [
      { Icon: Search, title: "Søgeordsanalyse", desc: "Find de søgninger, jeres kunder rent faktisk bruger." },
      { Icon: FileText, title: "Indholdsstrategi", desc: "Den rette plan for indhold, der rangerer." },
      { Icon: PenLine, title: "SEO-tekster", desc: "Tekster skrevet til både mennesker og Google." },
      { Icon: Settings, title: "Teknisk SEO", desc: "En hurtig, sund og crawlbar hjemmeside." },
      { Icon: Sparkles, title: "GEO / AI-synlighed", desc: "Bliv nævnt og citeret i ChatGPT, Perplexity og Googles AI-svar." },
      { Icon: TrendingUp, title: "Løbende optimering", desc: "Vedvarende vækst i både organisk og AI-drevet synlighed." },
    ],
    process: PROCESS,
    whoFor: WHO_FOR,
  },
  {
    slug: "strategi",
    navTitle: "Strategi",
    navDesc: "Kommunikations- og brandstrategi, der skaber retning og resultater.",
    Icon: Compass,
    heroTitle: "Kommunikation med retning",
    heroLead:
      "God kommunikation starter med en klar strategi. Vi hjælper jer med at finde den røde tråd mellem forretning, brand og kommunikation.",
    whatIsIt: [
      "Vi skaber sammenhæng mellem jeres forretningsmål og alt, hvad I kommunikerer.",
      "Målet er kommunikation, der understøtter virksomhedens forretningsmål - ikke tilfældige opslag uden retning.",
    ],
    benefits: [
      { Icon: Target, title: "Positionering", desc: "Find jeres unikke plads i markedet." },
      { Icon: Feather, title: "Brandfortælling", desc: "En stærk, sammenhængende historie." },
      { Icon: CompassIcon, title: "Kommunikationsstrategi", desc: "Retning for al jeres kommunikation." },
      { Icon: Quote, title: "Thought Leadership", desc: "Bliv en stemme, markedet lytter til." },
      { Icon: Radio, title: "PR-strategi", desc: "Planlæg synlighed i medierne." },
      { Icon: Grid3x3, title: "Contentstrategi", desc: "Det rette indhold til de rette kanaler." },
      { Icon: CalendarRange, title: "Kampagneplanlægning", desc: "Planlæg kampagner, der rammer forretningsmål." },
    ],
    process: PROCESS,
    whoFor: WHO_FOR,
  },
  {
    slug: "ai-agenter",
    navTitle: "AI Agenter",
    navDesc: "En intelligent digital kollega, der svarer, analyserer og automatiserer - døgnet rundt.",
    Icon: Bot,
    heroTitle: "Giv virksomheden ",
    heroTitleAccent: "en digital kollega",
    heroLead:
      "AI kan meget mere end at skrive tekster. Med en AI-agent får I en intelligent digital kollega, der kan besvare spørgsmål, analysere dokumenter, automatisere processer og hjælpe både medarbejdere og kunder - døgnet rundt.",
    whatIsIt: [
      "En AI-agent tager udgangspunkt i netop jeres virksomhed. Den lærer jeres dokumenter, processer og viden at kende, så den kan hjælpe med konkrete opgaver og levere hurtige, relevante svar.",
      "Målet er at frigøre tid, øge kvaliteten og gøre viden lettere tilgængelig.",
    ],
    benefits: [
      { Icon: Database, title: "Intern vidensbank", desc: "Svar på spørgsmål med udgangspunkt i virksomhedens egen viden." },
      { Icon: Headphones, title: "Kundeservice", desc: "Besvar kundehenvendelser hurtigere." },
      { Icon: TrendingUp, title: "Salgssupport", desc: "Hjælp sælgere med argumenter, produkter og dokumentation." },
      { Icon: FileText, title: "Dokumentanalyse", desc: "Find hurtigt svar i kontrakter, PDF'er og rapporter." },
      { Icon: GraduationCap, title: "Onboarding", desc: "Gør ny viden let tilgængelig for nye medarbejdere." },
      { Icon: RefreshCw, title: "Automatiske arbejdsgange", desc: "Lad AI håndtere rutineopgaver." },
    ],
    process: [
      { title: "Vi kortlægger behovet", desc: "Vi afdækker de opgaver, hvor AI kan skabe størst værdi." },
      { title: "Vi bygger AI-agenten", desc: "Vi udvikler løsningen med udgangspunkt i jeres data og arbejdsgange." },
      { title: "Vi tester og træner", desc: "Vi finjusterer agenten, så den leverer relevante og præcise svar." },
      { title: "Vi udvikler videre", desc: "AI-agenten bliver løbende klogere i takt med, at virksomheden udvikler sig." },
    ],
    whoFor: [
      "bruger meget tid på gentagne spørgsmål",
      "har store mængder intern viden",
      "ønsker hurtigere kundeservice",
      "vil effektivisere arbejdsgange",
      "ønsker AI, der tager udgangspunkt i jeres virksomhed",
    ],
  },
  {
    slug: "ai-implementering",
    navTitle: "AI Implementering & Automatisering",
    navDesc: "Vi identificerer, implementerer og automatiserer - så AI bliver en naturlig del af hverdagen.",
    Icon: Workflow,
    heroTitle: "Gør AI til ",
    heroTitleAccent: "en naturlig del af jeres virksomhed",
    heroLead:
      "AI skaber først værdi, når teknologien bliver en del af hverdagen. Vi hjælper virksomheder med at identificere, hvor AI kan gøre den største forskel, implementere de rigtige løsninger og automatisere manuelle arbejdsgange - så der bliver mere tid til det, mennesker er bedst til, og mindre tid på rutineopgaver.",
    whatIsIt: [
      "Mange virksomheder eksperimenterer med AI, men mangler en plan for, hvordan teknologien bliver en reel del af forretningen. Vi hjælper jer med at identificere de områder, hvor AI og automatisering kan skabe størst værdi, og omsætter mulighederne til konkrete løsninger, der fungerer i praksis.",
      "Det handler ikke om at implementere mest mulig AI - men om at implementere den rigtige AI.",
    ],
    benefits: [
      { Icon: Lightbulb, title: "AI-afklaring", desc: "Vi identificerer, hvor AI kan skabe størst værdi i netop jeres virksomhed." },
      { Icon: Settings, title: "Implementering af AI-værktøjer", desc: "Vi hjælper med at vælge, opsætte og integrere de løsninger, der passer til jeres behov." },
      { Icon: Zap, title: "Automatisering af arbejdsgange", desc: "Manuelle processer erstattes af intelligente workflows, der sparer tid og reducerer fejl." },
      { Icon: Bot, title: "AI Agenter", desc: "Vi udvikler digitale AI-agenter, der kan hjælpe medarbejdere og kunder døgnet rundt." },
      { Icon: Grid3x3, title: "Integration mellem systemer", desc: "Vi får jeres systemer til at arbejde sammen, så data flyder automatisk mellem dem." },
      { Icon: GraduationCap, title: "Træning og sparring", desc: "Vi sikrer, at medarbejderne får de nødvendige kompetencer til at udnytte AI i hverdagen." },
    ],
    process: [
      { title: "Vi analyserer virksomheden", desc: "Vi lærer jeres arbejdsgange, processer og forretningsmål at kende og identificerer de områder, hvor AI kan skabe størst værdi." },
      { title: "Vi udvikler løsningen", desc: "Vi udarbejder en konkret plan og vælger de AI-værktøjer og automatiseringer, der passer bedst til jeres virksomhed." },
      { title: "Vi implementerer", desc: "Vi opsætter løsningerne, integrerer dem med eksisterende systemer og sikrer, at de fungerer i praksis." },
      { title: "Vi udvikler videre", desc: "AI udvikler sig hurtigt. Derfor hjælper vi løbende med nye muligheder, optimeringer og videreudvikling af løsningerne." },
    ],
    whoFor: [
      "ønsker at arbejde mere effektivt",
      "bruger mange ressourcer på manuelle processer",
      "vil udnytte AI strategisk i virksomheden",
      "ønsker at automatisere tilbagevendende opgaver",
      "vil integrere AI i marketing, kommunikation eller kundeservice",
      "ønsker en sparringspartner, der kan omsætte AI til konkrete løsninger",
    ],
  },
];

export const SOLUTIONS_BY_SLUG = Object.fromEntries(SOLUTIONS.map((s) => [s.slug, s]));
export const CONTACT_EMAIL = "hello@aicom.dk";
export const BOOK_HREF = "mailto:hello@aicom.dk?subject=Book%20et%20m%C3%B8de%20med%20Aicom";
export const CONTACT_HREF = "mailto:hello@aicom.dk?subject=Kontakt%20-%20Aicom";
